import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  LuTrash2, LuMail, LuPhone, LuUser, LuGlobe, LuFlag,
  LuSearch, LuDownload, LuCircleCheck, LuX, LuCalendar, LuClock, LuHistory, LuUserPlus
} from 'react-icons/lu';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import ModalPortal from '../../components/ui/ModalPortal';
import EmailReminderModal from '../../components/EmailReminderModal';

const WebinarManagement = ({ onCountChange, onOpenAddModal }) => {
  const WEBINAR_CMS_SLUG = 'us-tax-return-webinar';
  const MAIN_SITE_URL = process.env.REACT_APP_MAIN_SITE_URL || 'http://localhost:3002';
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [reminderRecipient, setReminderRecipient] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'manual' | 'website'
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [dateFilterMode, setDateFilterMode] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pagePublished, setPagePublished] = useState(true);
  const [publicSlug, setPublicSlug] = useState(WEBINAR_CMS_SLUG);
  const [pageStatusLoading, setPageStatusLoading] = useState(true);
  const [togglingPage, setTogglingPage] = useState(false);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchRegistrations = async ({ showFullLoader = false } = {}) => {
    if (showFullLoader) setLoading(true);
    else setRefreshing(true);
    setError('');
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/webinar/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const data = response.data.data || [];
        setRegistrations(data);
        onCountChangeRef.current?.(data.length);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch registrations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchPageStatus = async () => {
    setPageStatusLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/admin/${WEBINAR_CMS_SLUG}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPagePublished(response.data.data?.isPublished !== false);
        setPublicSlug(response.data.data?.publicSlug || response.data.data?.slug || WEBINAR_CMS_SLUG);
      }
    } catch (err) {
      console.error('Failed to fetch webinar page status:', err);
    } finally {
      setPageStatusLoading(false);
    }
  };

  const togglePageStatus = async () => {
    setTogglingPage(true);
    const token = localStorage.getItem('adminToken');
    const nextPublished = !pagePublished;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/${WEBINAR_CMS_SLUG}`,
        { isPublished: nextPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPagePublished(nextPublished);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update webinar page status');
    } finally {
      setTogglingPage(false);
    }
  };

  useEffect(() => {
    fetchRegistrations({ showFullLoader: true });
    fetchPageStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/webinar/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRegistrations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete registration');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRegistrations.length && filteredRegistrations.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRegistrations.map((reg) => reg._id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} registration(s)?`)) return;

    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/webinar/admin/bulk-delete`,
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedIds([]);
      setSelectionMode(false);
      fetchRegistrations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete selected registrations');
    }
  };

  const filteredRegistrations = useMemo(() => {
    return registrations.filter((reg) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        reg.name?.toLowerCase().includes(q) ||
        reg.email?.toLowerCase().includes(q) ||
        reg.mobile?.toLowerCase().includes(q) ||
        reg.usTaxStatus?.toLowerCase().includes(q) ||
        reg.currentlyLiving?.toLowerCase().includes(q);

      if (!matchesSearch) return false;

      // Source Filter (All vs Manual vs Website)
      const isManual = Boolean(reg.isManual || reg.addedBy);
      if (sourceFilter === 'manual' && !isManual) return false;
      if (sourceFilter === 'website' && isManual) return false;

      if (dateFilterMode === 'today') {
        return moment(reg.createdAt).isSame(moment(), 'day');
      }
      if (dateFilterMode === 'yesterday') {
        return moment(reg.createdAt).isSame(moment().subtract(1, 'day'), 'day');
      }
      if (dateFilterMode === 'last7') {
        return moment(reg.createdAt).isAfter(moment().subtract(7, 'days').startOf('day'));
      }
      if (dateFilterMode === 'last30') {
        return moment(reg.createdAt).isAfter(moment().subtract(30, 'days').startOf('day'));
      }
      if (dateFilterMode === 'range') {
        let valid = true;
        if (startDate) {
          valid = valid && moment(reg.createdAt).isSameOrAfter(moment(startDate).startOf('day'));
        }
        if (endDate) {
          valid = valid && moment(reg.createdAt).isSameOrBefore(moment(endDate).endOf('day'));
        }
        return valid;
      }

      return true;
    });
  }, [registrations, searchQuery, sourceFilter, dateFilterMode, startDate, endDate]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sourceFilter, dateFilterMode, startDate, endDate]);

  const totalPages = Math.ceil(filteredRegistrations.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRegistrations = useMemo(() => {
    return filteredRegistrations.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredRegistrations, indexOfFirstItem, indexOfLastItem]);

  const formatSubmittedAt = (value) => moment(value).format('MMM DD, YYYY');
  const formatSubmittedTime = (value) => moment(value).format('hh:mm A');

  const csvData = useMemo(() => {
    return filteredRegistrations.map((reg) => ({
      Name: reg.name,
      Email: reg.email,
      Phone: `${reg.countryCode} ${reg.mobile}`.trim(),
      'Tax Status': reg.usTaxStatus,
      Location: reg.currentlyLiving,
      'Filed Before': reg.filedBefore,
      Questions: reg.questions,
      Date: formatSubmittedAt(reg.createdAt),
      Time: formatSubmittedTime(reg.createdAt),
      'Submitted At': moment(reg.createdAt).format('YYYY-MM-DD HH:mm:ss'),
      ...(reg.extraResponses || {}),
    }));
  }, [filteredRegistrations]);

  if (loading) {
    return <SkeletonLoader variant="table" rows={8} cols={5} />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-gray-100 dark:border-dark-700 bg-gray-50/80 dark:bg-dark-900/30 p-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">US Tax Webinar Page</p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            {pagePublished
              ? 'Registration page is live — users can open and submit the form.'
              : 'Registration page is closed — public page shows as unavailable.'}
          </p>
          <p className="text-xs text-primary-600 dark:text-primary-400 mt-2 font-medium">
            Public URL: {MAIN_SITE_URL}/{publicSlug}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${pagePublished ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {pageStatusLoading ? 'Loading...' : pagePublished ? 'Open' : 'Closed'}
          </span>
          <button
            type="button"
            onClick={togglePageStatus}
            disabled={pageStatusLoading || togglingPage}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-colors ${pagePublished
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
              } disabled:opacity-60`}
          >
            {togglingPage ? 'Updating...' : pagePublished ? 'Close Page' : 'Open Page'}
          </button>
        </div>
      </div>

      {refreshing && (
        <div className="text-xs font-semibold text-primary-600">Refreshing registrations...</div>
      )}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: registrations.length, icon: LuUser, iconBg: 'bg-primary-100 dark:bg-primary-900/30', iconColor: 'text-primary-600 dark:text-primary-400' },
          { label: 'US Citizens', value: registrations.filter((r) => r.usTaxStatus === 'US Citizen').length, icon: LuFlag, iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400' },
          { label: 'From USA', value: registrations.filter((r) => r.currentlyLiving === 'USA').length, icon: LuGlobe, iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400' },
          { label: 'Filtered', value: filteredRegistrations.length, icon: LuHistory, iconBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-600 dark:text-purple-400' },
        ].map((stat, i) => (
          <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-card">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${stat.iconBg}`}><stat.icon className={`w-5 h-5 ${stat.iconColor}`} /></div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-grow w-full flex items-center gap-3">
            <div className="relative flex-1">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search name, email, mobile..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-xs font-bold text-gray-800 dark:text-gray-200 cursor-pointer focus:ring-2 focus:ring-indigo-500/30 outline-none transition-all whitespace-nowrap"
            >
              <option value="all">All Entry Sources</option>
              <option value="manual">🏷️ Manual Entries Only</option>
              <option value="website">🌐 Website Submissions Only</option>
            </select>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowDateFilters(!showDateFilters)}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap cursor-pointer ${showDateFilters || dateFilterMode !== 'all'
                  ? "bg-primary-50 dark:bg-primary-900/30 text-primary-600 border border-primary-200 dark:border-primary-800"
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                }`}
            >
              <LuCalendar />
              Date Filters {dateFilterMode !== 'all' ? `(${dateFilterMode.toUpperCase()})` : ''}
            </button>
            <button
              onClick={() => setSelectionMode(!selectionMode)}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${selectionMode ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"}`}
            >
              <LuCircleCheck />
              {selectionMode ? "Active" : "Select"}
            </button>
            <CSVLink
              data={csvData}
              filename={`Webinar_Registrations_${moment().format('YYYY-MM-DD')}.csv`}
              className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap cursor-pointer"
            >
              <LuDownload size={18} />
              Export CSV
            </CSVLink>
          </div>
        </div>

        {showDateFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 flex flex-col md:flex-row gap-4 items-end animate-in fade-in duration-200">
            <div className="w-full md:w-auto">
              <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">Date Range Preset</label>
              <select
                value={dateFilterMode}
                onChange={(e) => setDateFilterMode(e.target.value)}
                className="w-full md:w-48 px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white cursor-pointer"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="range">Custom Range</option>
              </select>
            </div>

            {dateFilterMode === 'range' && (
              <>
                <div className="w-full md:w-auto">
                  <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">From Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="w-full md:w-auto">
                  <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">To Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            {dateFilterMode !== 'all' && (
              <button
                type="button"
                onClick={() => {
                  setDateFilterMode('all');
                  setStartDate('');
                  setEndDate('');
                }}
                className="px-4 py-2 text-xs font-bold text-red-600 dark:text-red-400 underline hover:no-underline cursor-pointer"
              >
                Reset Date Filter
              </button>
            )}
          </div>
        )}
      </div>

      {selectionMode && (
        <div className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-2xl">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
              {selectedIds.length} of {filteredRegistrations.length} selected
            </span>
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs font-bold text-primary-600 dark:text-primary-400 underline hover:no-underline cursor-pointer"
            >
              {selectedIds.length === filteredRegistrations.length && filteredRegistrations.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 transition-colors shadow-sm cursor-pointer"
            >
              <LuTrash2 size={16} /> Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                {selectionMode && (
                  <th className="px-4 py-4 text-center w-12">
                    <input
                      type="checkbox"
                      checked={selectedIds.length > 0 && selectedIds.length === filteredRegistrations.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-4 py-4 text-left text-sm font-semibold">Attendee</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Phone</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Submitted</th>
                <th className="px-4 py-4 text-center text-sm font-semibold w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filteredRegistrations.length === 0 ? (
                <tr>
                  <td colSpan={selectionMode ? 6 : 5} className="px-6 py-12 text-center text-sm text-gray-500">
                    No registrations found for the selected filters.
                  </td>
                </tr>
              ) : (
                currentRegistrations.map((reg) => (
                  <tr
                    key={reg._id}
                    className={`hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer ${selectedIds.includes(reg._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : reg.isManual ? 'bg-indigo-50/30 dark:bg-indigo-950/20' : ''}`}
                    onClick={() => selectionMode ? toggleSelectOne(reg._id) : setSelectedRegistration(reg)}
                  >
                    {selectionMode && (
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(reg._id)}
                          onChange={() => toggleSelectOne(reg._id)}
                          className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white text-sm whitespace-nowrap">{reg.name}</span>
                        {(reg.isManual || reg.addedBy) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-indigo-100 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shadow-xs" title={`Added by ${reg.addedBy || 'Admin'}`}>
                            🏷️ Manual
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-primary-600 dark:text-primary-400 whitespace-nowrap truncate max-w-[220px]" title={reg.email}>{reg.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap truncate max-w-[160px]" title={`${reg.countryCode || ''} ${reg.mobile || ''}`.trim()}>{`${reg.countryCode || ''} ${reg.mobile || ''}`.trim() || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatSubmittedAt(reg.createdAt)}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap w-28">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(reg._id); }}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-xl transition-colors cursor-pointer"
                          title="Delete Registration"
                        >
                          <LuTrash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setReminderRecipient({ name: reg.name, email: reg.email }); }}
                          className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 border border-primary-200 dark:border-primary-800/50 rounded-xl transition-colors cursor-pointer"
                          title="Set Email Reminder"
                        >
                          <LuMail className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
              Showing <span className="font-bold text-gray-900 dark:text-white">{indexOfFirstItem + 1}</span> to <span className="font-bold text-gray-900 dark:text-white">{Math.min(indexOfLastItem, filteredRegistrations.length)}</span> of <span className="font-bold text-gray-900 dark:text-white">{filteredRegistrations.length}</span> results
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-xl text-xs font-bold transition-all cursor-pointer ${currentPage === page ? 'bg-primary-600 text-white shadow-sm shadow-primary-500/30' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
                      >
                        {page}
                      </button>
                    );
                  }
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-1 text-xs text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ModalPortal open={Boolean(selectedRegistration)} onClose={() => setSelectedRegistration(null)}>
        {selectedRegistration && (
          <>
            <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Webinar Registration Details</h2>
              <button onClick={() => setSelectedRegistration(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl text-gray-500 dark:text-gray-300">
                <LuX />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Name</label><p className="font-bold text-gray-900 dark:text-white">{selectedRegistration.name}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Email</label><p className="font-bold text-primary-600">{selectedRegistration.email}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Phone</label><p className="font-bold text-gray-900 dark:text-white">{selectedRegistration.countryCode} {selectedRegistration.mobile}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Tax Status</label><p className="font-bold text-gray-900 dark:text-white">{selectedRegistration.usTaxStatus || '—'}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Location</label><p className="font-bold text-gray-900 dark:text-white">{selectedRegistration.currentlyLiving || '—'}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Filed Before</label><p className="font-bold text-gray-900 dark:text-white">{selectedRegistration.filedBefore || '—'}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl sm:col-span-2">
                  <label className="text-[10px] font-black uppercase text-gray-400">Submitted</label>
                  <p className="font-bold flex flex-wrap items-center gap-2 mt-1 text-gray-900 dark:text-white">
                    <LuCalendar className="w-4 h-4 text-gray-400" />
                    {formatSubmittedAt(selectedRegistration.createdAt)}
                    <LuClock className="w-4 h-4 text-gray-400" />
                    {formatSubmittedTime(selectedRegistration.createdAt)}
                  </p>
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl">
                <label className="text-[10px] font-black uppercase text-gray-400">Questions</label>
                <p className="font-medium italic leading-relaxed text-sm mt-1 text-gray-700 dark:text-gray-200">"{selectedRegistration.questions || 'No questions provided.'}"</p>
              </div>
              {selectedRegistration.extraResponses && Object.keys(selectedRegistration.extraResponses).length > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl space-y-2">
                  <label className="text-[10px] font-black uppercase text-blue-700 dark:text-blue-300">Additional Fields</label>
                  {Object.entries(selectedRegistration.extraResponses).map(([key, value]) => (
                    <p key={key} className="text-sm text-gray-800 dark:text-gray-200"><span className="font-bold capitalize">{key.replace(/_/g, ' ')}:</span> {String(value)}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3">
              <button type="button" onClick={() => setSelectedRegistration(null)} className="px-6 py-2 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 rounded-xl font-bold text-xs uppercase cursor-pointer">Close</button>
              <button type="button" onClick={() => { setReminderRecipient({ name: selectedRegistration.name, email: selectedRegistration.email }); setSelectedRegistration(null); }} className="px-6 py-2 bg-primary-600 text-white rounded-xl font-bold text-xs uppercase flex items-center gap-2 cursor-pointer"><LuMail className="w-4 h-4" /> Send Reminder</button>
              <button type="button" onClick={() => { handleDelete(selectedRegistration._id); setSelectedRegistration(null); }} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold text-xs uppercase cursor-pointer">Delete</button>
            </div>
          </>
        )}
      </ModalPortal>

      <EmailReminderModal
        isOpen={Boolean(reminderRecipient)}
        onClose={() => setReminderRecipient(null)}
        recipient={reminderRecipient}
      />
    </div>
  );
};

export default WebinarManagement;
